import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subDays, startOfDay, endOfDay, format } from "date-fns";
import { Flame, Wheat, Beef, Cookie } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { NutritionRings } from "./NutritionRings";
import { User } from '@supabase/supabase-js';
import { useEffect, useState } from "react";

const DAILY_GOALS = {
  calories: 2000,
  carbs: 275,
  protein: 180,
  fat: 67,
};

const WEEKLY_GOALS = {
  calories: DAILY_GOALS.calories * 7,
  carbs: DAILY_GOALS.carbs * 7,
  protein: DAILY_GOALS.protein * 7, 
  fat: DAILY_GOALS.fat * 7,
};

export function AnalyticsWeekly() {
  const [user, setUser] = useState<User | null>(null);
  
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();
  }, []);

  const { data: weeklyData, isLoading } = useQuery({
    queryKey: ["weekly-nutrition"],
    queryFn: async () => {
      const sevenDaysAgo = subDays(new Date(), 7);
      
      const { data: entries, error } = await supabase
        .from("v_food_entries_aggregated")
        .select("total_calories, total_carbs, total_protein, total_fat, consumed_at")
        .eq('user_id', user?.id)
        .gte("consumed_at", sevenDaysAgo.toISOString())
        .order("consumed_at", { ascending: false });

      if (error) throw error;

      // Initialize daily totals for the last 7 days
      const dailyTotals = [...Array(7)].map((_, index) => {
        const date = subDays(new Date(), index);
        const dayStart = startOfDay(date);
        const dayEnd = endOfDay(date);

        const dayEntries = entries?.filter(entry => {
          const entryDate = new Date(entry.consumed_at);
          return entryDate >= dayStart && entryDate <= dayEnd;
        }) || [];

        const totals = dayEntries.reduce(
          (acc, entry) => ({
            calories: acc.calories + (entry.total_calories || 0),
            carbs: acc.carbs + (entry.total_carbs || 0),
            protein: acc.protein + (entry.total_protein || 0),
            fat: acc.fat + (entry.total_fat || 0),
          }),
          { calories: 0, carbs: 0, protein: 0, fat: 0 }
        );

        return {
          date,
          data: [
            {
              nutrient: "Fat",
              label: `${Math.round(totals.fat)}g fat`,
              value: Math.min(Math.round((totals.fat / DAILY_GOALS.fat) * 100), 100),
              fill: "hsl(214, 84%, 53%)",
              total: Math.round(totals.fat)
            },
            {
              nutrient: "Protein",
              label: `${Math.round(totals.protein)}g protein`,
              value: Math.min(Math.round((totals.protein / DAILY_GOALS.protein) * 100), 100),
              fill: "hsl(0, 84%, 53%)",
              total: Math.round(totals.protein)
            },
            {
              nutrient: "Carbs",
              label: `${Math.round(totals.carbs)}g carbs`,
              value: Math.min(Math.round((totals.carbs / DAILY_GOALS.carbs) * 100), 100),
              fill: "hsl(47, 96%, 53%)",
              total: Math.round(totals.carbs)
            },
            {
              nutrient: "Calories",
              label: `${Math.round(totals.calories)} cal`,
              value: Math.min(Math.round((totals.calories / DAILY_GOALS.calories) * 100), 100),
              fill: "hsl(24, 96%, 53%)",
              total: Math.round(totals.calories)
            },
          ],
        };
      });

      // Calculate weekly totals
      const weeklyTotals = dailyTotals.reduce(
        (acc, day) => ({
          calories: acc.calories + (day.data[3].total || 0),
          carbs: acc.carbs + (day.data[2].total || 0),
          protein: acc.protein + (day.data[1].total || 0),
          fat: acc.fat + (day.data[0].total || 0),
        }),
        { calories: 0, carbs: 0, protein: 0, fat: 0 }
      );

      return {
        dailyTotals,
        weeklyTotals,
      };
    },
    enabled: !!user
  });

  if (isLoading || !weeklyData) {
    return null;
  }

  const weeklyRingsData = [
    {
      nutrient: "Fat",
      label: `${Math.round(weeklyData.weeklyTotals.fat)}g fat`,
      value: Math.min(Math.round((weeklyData.weeklyTotals.fat / WEEKLY_GOALS.fat) * 100), 100),
      fill: "hsl(214, 84%, 53%)",
      total: Math.round(weeklyData.weeklyTotals.fat)
    },
    {
      nutrient: "Protein", 
      label: `${Math.round(weeklyData.weeklyTotals.protein)}g protein`,
      value: Math.min(Math.round((weeklyData.weeklyTotals.protein / WEEKLY_GOALS.protein) * 100), 100),
      fill: "hsl(0, 84%, 53%)",
      total: Math.round(weeklyData.weeklyTotals.protein)
    },
    {
      nutrient: "Carbs",
      label: `${Math.round(weeklyData.weeklyTotals.carbs)}g carbs`,
      value: Math.min(Math.round((weeklyData.weeklyTotals.carbs / WEEKLY_GOALS.carbs) * 100), 100),
      fill: "hsl(47, 96%, 53%)",
      total: Math.round(weeklyData.weeklyTotals.carbs)
    },
    {
      nutrient: "Calories",
      label: `${Math.round(weeklyData.weeklyTotals.calories)} cal`,
      value: Math.min(Math.round((weeklyData.weeklyTotals.calories / WEEKLY_GOALS.calories) * 100), 100),
      fill: "hsl(24, 96%, 53%)",
      total: Math.round(weeklyData.weeklyTotals.calories)
    }
  ];

  return (
    <Card className="flex flex-col">
      <CardHeader className="items-center pb-2">
        <CardTitle>Weekly Nutrition</CardTitle>
        <CardDescription>Last 7 Days</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <NutritionRings data={weeklyRingsData} />
        <div className="flex w-max justify-between px-4 mt-6">
          {[...weeklyData.dailyTotals].reverse().map((day, index) => (
            <div key={index} className="flex flex-col items-center px-1">
              <p className="text-sm font-medium mb-0.5">
                {format(day.date, "EEE")}
              </p>
              <div className="w-[35px] h-[35px]">
                <NutritionRings data={day.data} size="sm" showLabel={false} />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="flex items-center gap-2 bg-orange-50 p-3 rounded-lg">
            <Flame className="h-5 w-5 text-orange-500" />
            <div>
              <p className="text-sm font-medium">{Math.round(weeklyData.weeklyTotals.calories)} cal</p>
              <p className="text-xs text-muted-foreground">of {WEEKLY_GOALS.calories} cal ({Math.round((weeklyData.weeklyTotals.calories / WEEKLY_GOALS.calories) * 100)}%)</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-yellow-50 p-3 rounded-lg">
            <Wheat className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-sm font-medium">{Math.round(weeklyData.weeklyTotals.carbs)}g</p>
              <p className="text-xs text-muted-foreground">of {WEEKLY_GOALS.carbs}g ({Math.round((weeklyData.weeklyTotals.carbs / WEEKLY_GOALS.carbs) * 100)}%)</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-red-50 p-3 rounded-lg">
            <Beef className="h-5 w-5 text-red-500" />
            <div>
              <p className="text-sm font-medium">{Math.round(weeklyData.weeklyTotals.protein)}g</p>
              <p className="text-xs text-muted-foreground">of {WEEKLY_GOALS.protein}g ({Math.round((weeklyData.weeklyTotals.protein / WEEKLY_GOALS.protein) * 100)}%)</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
            <Cookie className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm font-medium">{Math.round(weeklyData.weeklyTotals.fat)}g</p>
              <p className="text-xs text-muted-foreground">of {WEEKLY_GOALS.fat}g ({Math.round((weeklyData.weeklyTotals.fat / WEEKLY_GOALS.fat) * 100)}%)</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}